import { Modal, Spin } from "antd";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  AutoSizer,
  Grid,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import "./style.scss";
import { eventImageService } from "../../../services";
import ResponsiveImage from "../../../components/ResponsiveImage";
import { IEventImageModel } from "../../../models/interface";
import { WIDTH_MOBILE } from "../../../common/constant";

const EventModal = (props: any, ref: any) => {
  const [visible, setVisible] = useState(false);
  const [eventDetail, setEventDetail] = useState<IEventImageModel | null>(null);
  const [loading, setLoading] = useState(false);

  const { innerWidth } = window;
  const isMobile = innerWidth <= WIDTH_MOBILE;

  // Virtualization cache for auto height
  const imageCache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 300,
    })
  );

  useImperativeHandle(ref, () => ({
    openModal,
  }));

  const openModal = async (eventId: string) => {
    setVisible(true);
    setLoading(true);

    try {
      // Get event image by event_id
      const response = await eventImageService.getEventImage(eventId);

      if (response) {
        setEventDetail(response);
      }
    } catch (error) {
      console.error("Error fetching event image:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderVirtualizedImages = (event: IEventImageModel) => {
    const { name, details } = event;

    const cellRenderer = ({
      columnIndex,
      key,
      rowIndex,
      parent,
      style,
    }: any) => {
      const imageIndex = isMobile ? rowIndex : rowIndex * 2 + columnIndex;
      const image = details[imageIndex];
      const styles: {
        paddingLeft?: string;
        paddingRight?: string;
      } = {};

      if (!image) return null;

      if (!isMobile) {
        if (columnIndex === 0) {
          styles.paddingLeft = "0px";
        }
        if (columnIndex === 1) {
          styles.paddingRight = "0px";
        }
      }

      return (
        <CellMeasurer
          cache={imageCache.current}
          columnIndex={columnIndex}
          key={key}
          parent={parent}
          rowIndex={rowIndex}>
          {({ measure, registerChild }: any) => (
            <div ref={registerChild} style={style}>
              <div
                key={key}
                className={`event-image-item ${
                  rowIndex == 0 ? "event-image-item-first" : ""
                }`}
                onLoad={measure}
                style={styles}>
                <div className="event-image-container">
                  <ResponsiveImage
                    mediaId={image.picture_id || ""}
                    mobileMediaId={image.mobile_picture_id || ""}
                    alt={image.caption}
                    className="event-image"
                  />
                  {image.caption && (
                    <div className="image-caption">{image.caption}</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CellMeasurer>
      );
    };

    const columnCount = isMobile ? 1 : 2;
    const rowCount = isMobile ? details.length : Math.ceil(details.length / 2);

    return (
      <div key={event.year} className="event-item">
        <div className="event-year">
          <div className="event-year-title">
            <span className="icon">★</span>
            <span className="text">Năm {event.year}</span>
          </div>
        </div>

        <div className="event-content">
          <div className="event-location-group">
            <div className="event-location">
              <span className="icon">★</span>
              <span>{name}</span>
            </div>
            <div className="event-images-virtualized">
              <AutoSizer>
                {({ height, width }) => {
                  const columnWidth = isMobile ? width : Math.floor(width / 2);

                  return (
                    <Grid
                      cellRenderer={cellRenderer}
                      columnCount={columnCount}
                      columnWidth={columnWidth}
                      height={height}
                      rowCount={rowCount}
                      deferredMeasurementCache={imageCache.current}
                      rowHeight={imageCache.current.rowHeight}
                      width={width}
                      style={{ overflow: "auto" }}
                      enableFixedColumnScroll={false}
                      enableFixedRowScroll={false}
                      key={`grid-${width}-${height}-${isMobile}`}
                    />
                  );
                }}
              </AutoSizer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      onOk={() => setVisible(false)}
      width="100%"
      onCancel={() => setVisible(false)}
      footer={null}
      className="event-modal">
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "50px",
          }}>
          <Spin size="large" />
        </div>
      ) : (
        eventDetail && renderVirtualizedImages(eventDetail)
      )}
    </Modal>
  );
};

export default forwardRef(EventModal);
